import React, { useState, useEffect } from 'react';
import { Card, Steps, Icon } from 'antd';
import useDomainSetupProgress from '../../process/hooks/useDomainSetupProgress';
import { AddNewWebsiteIndexEntryParams } from '../types';
import buildWebsiteIndexEntryParams from './buildWebsiteIndexEntryParams';


const { Step } = Steps;

// Safety net: if setup hasn't finished by then, stop waiting and add the domain with whatever data arrived.
const SAFETY_NET_TIME_LIMIT_MS = 120 * 1000;

type Props = {
    domain: string
    processGuid: string
    onSetupFinish: (params: AddNewWebsiteIndexEntryParams, keywords: string[]) => Promise<void>;
}
type DomainValidationStepProps = {
    domain: string;
    searchEngineSettings: Record<string, string> | null;
    isDoneWaiting: boolean;
}
type CompetitorsStepProps = {
    currentStep: number;
    competitors: string[] | null;
    isSkipped: boolean;
}
type KeywordsStepProps = {
    currentStep: number;
    keywords: string[] | null;
    isSkipped: boolean;
}
type MonitoringRegionsStepProps = {
    currentStep: number;
    regions: string[] | null;
    isSkipped: boolean;
}

function DomainValidationStep({ domain, searchEngineSettings, isDoneWaiting }: DomainValidationStepProps) {
    return (
        <>
            {searchEngineSettings === null && !isDoneWaiting && <div className="pending">
                Validating your domain...
                <Icon className="loader" type="loading" />
            </div>}
            {(searchEngineSettings || isDoneWaiting) && <h4>{domain} is a valid domain</h4>}
        </>
    );
}


function CompetitorsStep({ currentStep, competitors, isSkipped }: CompetitorsStepProps) {
    if (isSkipped) {
        return <h4>No data for competitors. You can add that later.</h4>;
    }

    return (
        <>
            {competitors === null && currentStep === 0 && <h4>We'll check for competitors</h4>}
            {competitors === null && currentStep === 1 && <div className="pending">
                Checking for competitors...
                <Icon className="loader" type="loading" />
            </div>}
            {competitors && competitors.length === 0 && <h4>No competitors found. You can set them up later!</h4>}
            {competitors && competitors.length > 0 && (
                <>
                    <h4>We'll track your SEO performance against these competitors:</h4>
                    <ul className="competitors-list">
                        {competitors.map((competitor, index) => (
                            <li key={index}>{competitor}</li>
                        ))}
                    </ul>
                </>
            )}
        </>
    );
}

function KeywordsStep({ currentStep, keywords, isSkipped }: KeywordsStepProps) {
    if (isSkipped) {
        return <h4>No data for keywords. You can add that later.</h4>;
    }

    return (
        <>
            {(keywords === null && currentStep !== 2) && <h4>We'll check for ranking keywords</h4>}
            {(keywords === null && currentStep === 2) && <div className="pending">
                Checking for ranking keywords...
                <Icon className="loader" type="loading" />
            </div>}
            {keywords && keywords.length === 0 && <h4>No keywords found. You can set them up later!</h4>}
            {keywords && keywords.length > 0 && (
                <>
                    <h4>We'll track your rankings for these keywords:</h4>
                    <div className="keywords">
                        {keywords.join(", ")}
                    </div>
                </>
            )}
        </>
    );
}

function MonitoringRegionsStep({ currentStep, regions, isSkipped }: MonitoringRegionsStepProps) {
    const regionsMap: Record<string, string> = {
        'us-east-1': 'US East (N. Virginia)',
        'us-west-2': 'US West (Oregon)',
        'eu-west-2': 'Europe, UK',
        'eu-west-1': 'Europe, Ireland',
        'eu-central-1': 'Europe, Germany',
        'eu-west-3': 'Europe, France',
        'ca-central-1': 'Canada',
        'sa-east-1': 'Brazil',
        'ap-northeast-1': 'Japan',
        'ap-southeast-1': 'Singapore',
        'ap-southeast-2': 'Australia',
        'ap-south-1': 'India'
    };

    if (isSkipped) {
        return <h4>No data for loading speed monitoring regions. We'll monitor from {regionsMap['us-east-1']}, you can change that later.</h4>;
    }

    return (
        <>
            {(regions === null && currentStep !== 3) && <h4>We'll select monitoring regions</h4>}
            {(regions === null && currentStep === 3) && <div className="pending">
                Selecting monitoring regions...
                <Icon className="loader" type="loading" />
            </div>}
            {regions && regions.length === 0 && <h4>No monitoring region selected. You can add them later!</h4>}
            {regions && regions.length > 0 && (
                <>
                    <h4>We'll monitor your website's loading speed from these regions:</h4>
                    <ul className="regions-list">
                        {regions.map((region, index) => (
                            regionsMap[region] ? <li key={index}>{regionsMap[region]}</li> : null
                        ))}
                    </ul>
                </>
            )}
        </>
    );
}

export default function DomainSetupProgress({ domain, processGuid, onSetupFinish }: Props) {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const { homepageUrl, competitors, topKeywords, monitoringRegions, searchEngineMarket, processError, hasFinished, isLoading } = useDomainSetupProgress(domain, processGuid);
    const [lastStepDescription, setLastStepDescription] = useState<string | null>(null);
    const [hasReachedSafetyNet, setHasReachedSafetyNet] = useState<boolean>(false);
    const competitorDomains = competitors ? competitors.map(c => c.domain) : null;

    // Missing data stops being "on its way" once the process has finished or the safety net fires.
    const isDoneWaiting = hasFinished || hasReachedSafetyNet;

    useEffect(() => {
        const safetyNetTimer = setTimeout(() => setHasReachedSafetyNet(true), SAFETY_NET_TIME_LIMIT_MS);
        return () => clearTimeout(safetyNetTimer);
    }, []);

    useEffect(() => {
        if (!isLoading || hasReachedSafetyNet) {
            if (currentStep === 0 && ((homepageUrl !== null && searchEngineMarket !== null) || isDoneWaiting)) {
                setCurrentStep(1);
            } else if (currentStep === 1 && (competitors !== null || isDoneWaiting)) {
                setCurrentStep(2);
            } else if (currentStep === 2 && (topKeywords !== null || isDoneWaiting)) {
                setCurrentStep(3);
            } else if (currentStep === 3 && (monitoringRegions !== null || isDoneWaiting)) {
                setCurrentStep(4);
                setLastStepDescription("All set! We are starting a full SEO Analysis for your domain and you'll be redirected to your dashboard.");

                (async () => {
                    try {
                        const params: AddNewWebsiteIndexEntryParams = buildWebsiteIndexEntryParams(domain, {
                            homepageUrl,
                            competitors: competitorDomains,
                            monitoringRegions,
                            searchEngineMarket,
                        });
                        const keywords = topKeywords || [];

                        await onSetupFinish(params, keywords);
                    } catch (err) {
                        console.error("Error in onSetupFinish:", err);
                    }
                })();
            }
        }
    }, [isLoading, hasReachedSafetyNet, isDoneWaiting, currentStep, homepageUrl, competitors, topKeywords, monitoringRegions, searchEngineMarket, onSetupFinish, domain, competitorDomains]);

    return (
        <Card bordered={false} className="domain-setup-card">
            <div className="domain-setup-container">
                <Steps className="domain-setup-steps" direction="vertical" size="small" current={currentStep}>
                    <Step
                        title="Domain Validation"
                        description={
                            <DomainValidationStep domain={domain} searchEngineSettings={searchEngineMarket} isDoneWaiting={isDoneWaiting} />
                        }
                    />

                    <Step
                        title="Competitors"
                        description={
                            <CompetitorsStep currentStep={currentStep} competitors={competitorDomains} isSkipped={(competitorDomains === null && isDoneWaiting) || !!processError?.competitors} />
                        }
                    />

                    <Step
                        title="Top Keywords"
                        description={
                            <KeywordsStep currentStep={currentStep} keywords={topKeywords && topKeywords?.slice(0, 10)} isSkipped={(topKeywords === null && isDoneWaiting) || !!processError?.top_keywords} />
                        }
                    />

                    <Step
                        title="Loading Speed Monitoring Regions"
                        description={
                            <MonitoringRegionsStep currentStep={currentStep} regions={monitoringRegions} isSkipped={(monitoringRegions === null && isDoneWaiting) || !!processError?.uptime_monitoring_regions} />
                        }
                    />

                    <Step
                        title="Finish Setup"
                        description={lastStepDescription}
                    />
                </Steps>

                <div className="progress-screen">
                    <img src="/images/illustration-progress.svg" alt="gathering website settings" />
                    <h2>We're setting up your domain</h2>
                    <span className="description">This usually takes 30-45 seconds</span>
                    {/* <Icon className="loader" type="loading" /> */}
                </div>
            </div>
        </Card>
    );
}
